// Libraries
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { injectIntl, FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { Row, Col } from 'antd';

// Components
import RequestEmailCheckbox from './RequestEmailCheckbox';
import { AppearRight } from 'certn-ui/Animate';
import Title from 'certn-ui/Title';
import { TrackNavigation, AgreementSigning } from 'views/welcome/components';
import Text from 'certn-ui/Text';
import Button from 'certn-ui/Button';

import config from 'utils/config';

// Actions & Selectors
import { getInformation, getIsOnboardingTypeHR, getOnboardingId } from 'views/welcome/WelcomeSelectors';
import { getLaunchDarklyFlags } from 'base/BaseSelectors';

const DisclosureForm = ({ handleSubmit }) => {
    const intl = useIntl();

    const { disclosure_accepted, requested_disclosure_email } = useSelector(getInformation);
    const isOnboardingTypeHR = useSelector(getIsOnboardingTypeHR);
    const onboardingId = useSelector(getOnboardingId);

    const { webFeatureHideDisclosureCheckBoxForMyCrcUsApplicants } = useSelector(getLaunchDarklyFlags);
    const [isSigned, setIsSigned] = useState(false);
    const [showAgreementSigning, setShowAgreementSigning] = useState(false);
    const [hasRequestedEmail, setHasRequestedEmail] = useState(requested_disclosure_email);

    const handleOpenAgreementSigning = () => setShowAgreementSigning(true);
    const handleCloseAgreementSigning = () => setShowAgreementSigning(false);
    const onAgreementSigningFinish = () => setIsSigned(true);

    const handleNextButtonClick = () => handleSubmit(hasRequestedEmail);

    const documentTitle = isOnboardingTypeHR ? (
        <FormattedMessage id="welcome.Disclosure.HRScreeningPackage" defaultMessage="Employment Screening Package" />
    ) : (
        <FormattedMessage id="welcome.Disclosure.PMScreeningPackage" defaultMessage="Tenancy Screening Package" />
    );

    return (
        <AppearRight>
            <Title>
                <FormattedMessage
                    id="welcome.Disclosure.title"
                    defaultMessage="We need your consent to run a US criminal record check"
                />
            </Title>
            <Row gutter={[16, 16]}>
                <Col sm={24} md={{ span: 14, offset: 5 }} lg={{ span: 12, offset: 6 }} xl={{ span: 10, offset: 7 }}>
                    <Text align="center" color="certnGray600">
                        <FormattedMessage
                            id="welcome.Disclosure.subtitle"
                            defaultMessage="The following documents must be electronically signed for the background check process to continue."
                        />
                    </Text>
                </Col>
            </Row>
            <Row style={{ padding: '16px 0' }}>
                <Col span={24} md={{ span: 16, offset: 4 }} lg={{ span: 16, offset: 4 }}>
                    <AgreementSigning
                        isOpen={showAgreementSigning}
                        onOpen={handleOpenAgreementSigning}
                        onClose={handleCloseAgreementSigning}
                        documentTitle={documentTitle}
                        isSigned={disclosure_accepted || isSigned}
                        endpointURL={`/onboarding/${onboardingId}/signing_urls/`}
                        onFinish={onAgreementSigningFinish}
                    />
                </Col>
            </Row>
            {!webFeatureHideDisclosureCheckBoxForMyCrcUsApplicants && (
                <Row style={{ paddingTop: '16px', paddingBottom: '32px' }}>
                    <Col sm={24} md={{ span: 14, offset: 5 }} lg={{ span: 14, offset: 5 }}>
                        <RequestEmailCheckbox
                            hasRequestedEmail={hasRequestedEmail}
                            setHasRequestedEmail={setHasRequestedEmail}
                        />
                    </Col>
                </Row>
            )}
            {disclosure_accepted || isSigned ? (
                <TrackNavigation handleSubmit={handleNextButtonClick} />
            ) : (
                <>
                    <TrackNavigation
                        nextButtonTitle={intl.formatMessage({
                            id: 'disclosure.signDocuments',
                            defaultMessage: 'Sign Documents',
                        })}
                        handleSubmit={handleOpenAgreementSigning}
                    />
                    {config.isNotProduction && (
                        <Button size="large" type="dashed" onClick={() => handleSubmit(hasRequestedEmail)}>
                            {intl.formatMessage({ id: 'button.demoSkip', defaultMessage: 'Demo Skip' })}
                        </Button>
                    )}
                </>
            )}
        </AppearRight>
    );
};

DisclosureForm.propTypes = {
    orgName: PropTypes.string,
    handleSubmit: PropTypes.func.isRequired,
};

DisclosureForm.defaultProps = {
    orgName: 'Certn',
};

export default injectIntl(DisclosureForm);
